import { useEffect, useMemo, useRef, useState } from 'react';
import { getLocalTimeZone, today } from '@internationalized/date';
import { Trans, useLingui } from '@lingui/react/macro';
import { parseISO } from 'date-fns';
import debounce from 'lodash/debounce';

import FileUpload from '@/components/FileUpload';
import { InputCountry } from '@/components/InputCountry/InputCountry';
import InputDate from '@/design_system/InputDate';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import InputText from '@/design_system/InputText';
import { Label } from '@/design_system/Label/Label';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import { useUpdateArticle } from '@/models/article';
import { Medium } from '@/models/medium';
import { useColorOptions } from '@/models/product';
import { ArticleWithRelations } from '@/models/request';
import { useSizesOptionsSorted } from '@/models/sizeProduct';
import { WarrantyAlert } from '@/routes/Requests/Request/Article/WarrantyAlert';
import { useDatabaseTranslation } from '@/services/i18n';

export const AdditionalInfo = ({
  article,
  proofOfPurchaseMedia,
}: {
  article: ArticleWithRelations;
  proofOfPurchaseMedia: Medium[];
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { request, view } = useBrandRequestContext();
  const { sizesOptions, sizeOptionValue, sizeOptionLabel } = useSizesOptionsSorted({ article });

  const colorOptions = useColorOptions({ article });

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const [purchaseDate, setPurchaseDate] = useState(article.purchaseDate);
  const [purchaseLocation, setPurchaseLocation] = useState(article.purchaseLocation ?? '');
  const [orderNumber, setOrderNumber] = useState(article.orderNumber ?? '');

  const { mutate: updateArticle } = useUpdateArticle(
    {
      articleId: article.id,
      requestId: request.id,
    },
    {
      optimistic: true,
    }
  );

  const debouncedUpdateArticle = useMemo(() => {
    return debounce(updateArticle, 500);
  }, [updateArticle]);

  const handleBlurInputDate = () => {
    if (purchaseDate && parseISO(purchaseDate) <= new Date()) {
      if (purchaseDate !== article.purchaseDate) {
        updateArticle({
          data: {
            purchaseDate,
          },
        });
      }
    } else {
      updateArticle({
        data: {
          purchaseDate: null,
        },
      });
      setPurchaseDate(null);
    }
  };

  const enableWarrantyFieldsRequiredConfig = !!article.activeWarrantyType;
  const showColor = view.articles.details.color.shown && colorOptions.length > 0;
  const showSize = view.articles.details.size.shown && sizesOptions.length > 0;
  const showWarranty =
    view.articles.warranty.shown &&
    (showColor ? !!article.color : true) &&
    (showSize ? !!article.size : true);

  return (
    <Stack gap="1.5rem" ref={ref}>
      {showColor && (
        <InputSelect
          variant="select"
          label={
            <>
              {t({ id: 'client.new.article.form.color.label', message: 'Color' })}
              {!view.articles.details.color.required && <OptionalLabel />}
            </>
          }
          placeholder={t({
            id: 'client.new.article.form.color.placeholder',
            message: 'Select a color...',
          })}
          isSearchable={false}
          options={colorOptions}
          value={colorOptions.find((colorOption) => article.color?.en === colorOption.en)}
          getOptionValue={(colorOption) => colorOption.en}
          getOptionLabel={(colorOption) => _db(colorOption)}
          onChange={(colorOption) => {
            if (colorOption) {
              updateArticle({ data: { color: colorOption } });
            }
          }}
          styleVariant="brand"
          size="large"
          style={{ flex: 1 }}
        />
      )}

      {showSize && (
        <InputSelect
          variant="select"
          label={
            <>
              {t({ id: 'client.new.article.form.size.label', message: 'Size' })}
              {!view.articles.details.size.required && <OptionalLabel />}
            </>
          }
          placeholder={t({
            id: 'client.new.article.form.size.placeholder',
            message: 'Select a size...',
          })}
          isSearchable={false}
          options={sizesOptions}
          value={sizesOptions.find((sizeOption) => article.size?.en === sizeOption.en)}
          getOptionValue={sizeOptionValue}
          getOptionLabel={sizeOptionLabel}
          onChange={(sizeOption) => {
            if (sizeOption) {
              updateArticle({ data: { size: sizeOption } });
            }
          }}
          styleVariant="brand"
          size="large"
          style={{ flex: 1 }}
        />
      )}

      {showWarranty && (
        <>
          <Stack gap="0.5rem">
            <Label
              label={t({
                id: 'client.new.article.form.warranty.label',
                message: 'Warranty',
              })}
              size="large"
            />
            {(view.articles.warranty.fields.purchaseDate.shown || article.activeWarrantyType) && (
              <WarrantyAlert article={article} isDiagForm />
            )}
          </Stack>

          {view.articles.warranty.fields.purchaseDate.shown && (
            <InputDate
              label={
                <>
                  <Trans id="client.new.article.form.purchase-date.label">Purchase date</Trans>
                  {!(
                    enableWarrantyFieldsRequiredConfig &&
                    view.articles.warranty.fields.purchaseDate.required
                  ) && <OptionalLabel />}
                </>
              }
              value={purchaseDate}
              onChange={(date) => setPurchaseDate(date ?? null)}
              maxValue={today(getLocalTimeZone())}
              style={{ flex: 1 }}
              onBlur={handleBlurInputDate}
              size="large"
              variant="brand"
              error={
                !!purchaseDate && parseISO(purchaseDate) > new Date()
                  ? t({
                      id: 'client.new.article.form.purchase-date.error',
                      message: 'The purchase date must be in the past',
                    })
                  : undefined
              }
            />
          )}

          {view.articles.warranty.fields.purchaseLocation.shown && (
            <InputText
              label={
                <>
                  <Trans id="client.new.article.form.purchase-location.label">
                    Purchase location
                  </Trans>
                  {!(
                    enableWarrantyFieldsRequiredConfig &&
                    view.articles.warranty.fields.purchaseLocation.required
                  ) && <OptionalLabel />}
                </>
              }
              placeholder={t({
                id: 'client.new.article.form.purchase-location.placeholder',
                message: 'Add a purchase location...',
              })}
              size="large"
              value={purchaseLocation}
              onChange={(value) => {
                setPurchaseLocation(value);
                debouncedUpdateArticle({
                  data: {
                    purchaseLocation: value.trim() || null,
                  },
                });
              }}
              style={{ flex: 1 }}
            />
          )}

          {view.articles.warranty.fields.purchaseCountry.shown && (
            <InputCountry
              label={
                <>
                  <Trans id="client.new.article.form.purchase-country.label">
                    Purchase country
                  </Trans>
                  {!(
                    enableWarrantyFieldsRequiredConfig &&
                    view.articles.warranty.fields.purchaseCountry.required
                  ) && <OptionalLabel />}
                </>
              }
              size="large"
              styleVariant="brand"
              value={article.purchaseCountry}
              onChange={(newPurchaseCountry) => {
                updateArticle({
                  data: {
                    purchaseCountry: newPurchaseCountry,
                  },
                });
              }}
            />
          )}

          {view.articles.warranty.fields.purchaseChannel.shown && (
            <InputSelect
              variant="select"
              label={
                <>
                  {t({
                    id: 'client.new.article.form.purchase-channel.label',
                    message: 'Purchase channel',
                  })}
                  {!(
                    enableWarrantyFieldsRequiredConfig &&
                    view.articles.warranty.fields.purchaseChannel.required
                  ) && <OptionalLabel />}
                </>
              }
              placeholder={t({
                id: 'client.new.article.form.purchase-channel.placeholder',
                message: 'Select a purchase channel...',
              })}
              isSearchable={false}
              options={view.articles.warranty.fields.purchaseChannel.options}
              value={article.purchaseChannel}
              getOptionValue={(option) => option.en}
              getOptionLabel={(option) => _db(option)}
              onChange={(option) => {
                updateArticle({ data: { purchaseChannel: option ?? null } });
              }}
              isClearable
              styleVariant="brand"
              size="large"
              style={{ flex: 1 }}
            />
          )}

          {view.articles.warranty.fields.orderNumber.shown && (
            <InputText
              label={
                <>
                  <Trans id="client.new.article.form.order-number.label">Order number</Trans>
                  {!(
                    enableWarrantyFieldsRequiredConfig &&
                    (view.articles.warranty.fields.orderNumber.required ||
                      view.articles.warranty.fields.proofOfPurchaseOrOrderNumber.required)
                  ) && <OptionalLabel />}
                </>
              }
              placeholder={t({
                id: 'client.new.article.form.order-number.placeholder',
                message: 'Add an order number...',
              })}
              size="large"
              value={orderNumber}
              onChange={(value) => {
                setOrderNumber(value);
                debouncedUpdateArticle({
                  data: {
                    orderNumber: value.trim() || null,
                  },
                });
              }}
              style={{ flex: 1 }}
            />
          )}

          {view.articles.warranty.fields.proofOfPurchase.shown && (
            <FileUpload
              uploadData={{
                type: 'proof-of-purchase',
                articleId: article.id,
              }}
              type="file"
              ariaLabel={t({
                id: 'client.new.article.form.proof-of-purchase.label',
                message: 'Proof of purchase',
              })}
              label={
                <>
                  <Trans id="client.new.article.form.proof-of-purchase.label">
                    Proof of purchase
                  </Trans>
                  {!(
                    enableWarrantyFieldsRequiredConfig &&
                    (view.articles.warranty.fields.proofOfPurchase.required ||
                      view.articles.warranty.fields.proofOfPurchaseOrOrderNumber.required)
                  ) && <OptionalLabel />}
                </>
              }
              media={proofOfPurchaseMedia}
              deleteWithApi
              size="xlarge"
              theme="brand"
              onMount={() => {
                window.scroll({
                  top: document.body.scrollHeight,
                  behavior: 'smooth',
                });
              }}
            />
          )}
        </>
      )}
    </Stack>
  );
};
